/**
 * THE ONLY PLACE THIS APP TALKS TO THE SERVER.
 *
 * Contract (the API is not running yet; this is written against it, not discovered from it):
 *
 *   • Same-origin `/api/*`, proxied to the API in dev (`vite.config.ts`).
 *   • The session is a first-party cookie → `credentials: 'same-origin'`. No token is ever read,
 *     stored or attached by this app.
 *   • 401 ⇒ there is no session. The app routes to sign-in. It does not guess why.
 *   • 402 / 403 ⇒ the gate denied. The body is `DenyBody` (packages/contracts/src/decision.ts).
 *     It is surfaced as a `DeniedError` and rendered by <DenialNotice>. Never as a raw 403.
 *   • 404 ⇒ `NotFoundError`; the shell renders the Hebrew "הדף שחיפשת לא קיים" state (09 §5.8).
 *   • Any other non-2xx ⇒ `ApiError` carrying `requestUid` only. 09 §9.1 V3: the user is shown a
 *     short reference code and never a route, a path, an exception type or a status code.
 *
 * RETRY POLICY — binding: a write is NEVER retried. Not on a network error, not on a timeout, not
 * on a 5xx. The client cannot know whether the server committed before the connection died, and a
 * duplicated פק' יומן or a duplicated send-to-Priority is the highest blast radius in the product
 * (09 §2.3 row 14). Idempotent reads retry; everything else surfaces the failure to the user.
 *
 * There is NO permission logic in this file. It reports what the gate decided.
 */

import { REVOCATION_CLASSES } from './contract';
import type { AuthzStage, DenyBody, DenyReason, RevocationTally } from './contract';

/**
 * Exported for `operations.ts` only. The operation table holds the DECLARED path — the one in
 * `registry/routes.baseline.json`, `/api`-prefixed — so that the table and the baseline can be
 * compared field for field; the prefix is then removed once, here, rather than being absent from the
 * table and therefore unverifiable against the artifact the server generates.
 */
export const API_BASE = '/api';

/** RFC 9110 safe methods. Only these are ever retried. */
const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

export class ApiError extends Error {
  readonly status: number;
  /** The short reference code a user may quote to support (09 §5.8). */
  readonly requestUid: string;
  constructor(status: number, requestUid: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestUid = requestUid;
  }
}

/** 401. The app routes to sign-in. It never infers *why* the session is absent. */
export class UnauthenticatedError extends ApiError {
  constructor(requestUid: string) {
    super(401, requestUid, 'unauthenticated');
    this.name = 'UnauthenticatedError';
  }
}

/** 402 / 403. Carries the gate's body verbatim; <DenialNotice> turns it into Hebrew. */
export class DeniedError extends ApiError {
  readonly body: DenyBody;
  constructor(status: number, body: DenyBody) {
    super(status, body.requestUid, `denied:${body.reason ?? 'uniform'}`);
    this.name = 'DeniedError';
    this.body = body;
  }
  /** `undefined` outside the tenant — that absence is the contract, not missing data. */
  get reason(): DenyReason | undefined {
    return this.body.reason;
  }
}

/**
 * 403 THAT IS NOT A DENIAL — the step-up.
 *
 * `auth.mfa.enrol` answers `{ error: 'step_up_required', need }` with a 403, and it is NOT a
 * `DenyBody`: there is no `reason`, no `stage`, and the GATE DID NOT PRODUCE IT. The handler did,
 * after the gate allowed the request, because the session is too old or a second factor has not been
 * presented. Routing it through `DeniedError` would drop `need` — `parseDenyBody` keeps only the
 * gate's four fields — and a step-up with no `need` is the locked door the enrolment route was added
 * to remove.
 *
 * `need` is safe to disclose. The route says why: the caller is already authenticated, and telling
 * them WHICH challenge to satisfy is the difference between a usable product and a wall.
 */
export class StepUpRequiredError extends ApiError {
  /** `'REAUTH' | 'MFA' | 'ENROLMENT'`, validated into a union by `stepUpText.ts`. Never rendered raw. */
  readonly need: string | null;
  constructor(requestUid: string, need: string | null) {
    super(403, requestUid, 'step_up_required');
    this.name = 'StepUpRequiredError';
    this.need = need;
  }
}

export class NotFoundError extends ApiError {
  constructor(requestUid: string) {
    super(404, requestUid, 'not_found');
    this.name = 'NotFoundError';
  }
}

/**
 * 409 — THE GATE ALLOWED IT AND THE WRITE STILL COULD NOT HAPPEN.
 *
 * ── WHY THIS CLASS EXISTS, HAVING DELIBERATELY NOT EXISTED ──────────────────────────────────────
 *
 * The previous round recorded the absence as one of three cuts: *"api/client.ts keeps only a support
 * reference from a conflict body, and its taxonomy is a binding contract that a screen may not widen
 * on its own."* That was right at the time — the server was dropping `excess` one line after
 * computing it, so widening the client would have carried nothing.
 *
 * The server now carries it. `routes/tenant-lifecycle.ts` and `routes/customer-admin.ts` both put
 * `excess` on the 409 body, the source routes put `missingConfiguration` on theirs, the lifecycle
 * route puts `allowedTransitions` on its own, and `refuse()` in `routes/commercial.ts` puts
 * `currentStatus` and `permittedFrom` on its. Every one of those is the ACTIONABLE HALF of a refusal,
 * and the Owner's direction is explicit that they may not be collapsed into a generic 409.
 *
 * ── WHAT THIS DOES NOT DO ───────────────────────────────────────────────────────────────────────
 *
 * It does not widen the DENIAL taxonomy by one member. A 402/403 is still a `DeniedError` carrying a
 * `DenyBody`, still rendered by `<DenialNotice>`, still the only shape in which an ACCESS decision
 * reaches this app. A conflict is not an access decision — the caller held the capability and was
 * inside their tenant — and keeping the two classes apart is what stops a screen from treating "you
 * may not" and "it cannot be done from here" as one sentence.
 *
 * And it decides nothing. `body` is data the server produced; every field is turned into Hebrew by a
 * pure mapper before any screen sees it, and no screen renders a member of it directly.
 */
export interface ConflictBody {
  /** The server's own reason code. Mapped to Hebrew; NEVER rendered (09 §9.1 V3). */
  readonly error: string;
  /** Capabilities a delegation ceiling refused, or the closed set an assertion was measured against. */
  readonly excess?: readonly string[];
  /** Every absent configuration field, in the server's order. Never the first only (07 N-5). */
  readonly missingConfiguration?: readonly string[];
  /** Where the object actually is, and where it may go from there. */
  readonly from?: string;
  readonly currentStatus?: string;
  readonly allowedTransitions?: readonly string[];
  readonly permittedFrom?: readonly string[];
  readonly permittedStates?: readonly string[];
  /** Capacity: the count, the limit, and the address that resolves it (08 §7). */
  readonly current?: number;
  readonly limit?: number;
  readonly upgradePath?: string;
  /** A conflict between candidates, and what would settle it (0002 rule 3). */
  readonly resolvingFactRequired?: string;
  readonly candidateCount?: number;
  /** The service's own sentence, used only where this build has no Hebrew for `error`. */
  readonly remedy?: string;
  readonly detail?: string;
  // ── Offboarding (`refuse()` in `routes/tenant-lifecycle.ts`) ────────────────────────────────────────
  /** Which offboarding stage the tenant is in, and which it may move to from there. */
  readonly currentStage?: string;
  readonly allowedStages?: readonly string[];
  /** The service that owns the arc the caller asked for. A code; a mapper turns it into words. */
  readonly arcOwner?: string;
  /** Every reason the retention policy did not hold together. Never the first only. */
  readonly violations?: readonly string[];
  readonly reinstatableUntil?: string;
  readonly notBefore?: string;
  /** The commercial half's own refusal, carried through rather than flattened. */
  readonly commercialReason?: string;
  /**
   * WHAT BECAME UNREACHABLE BEFORE THE REFUSAL.
   *
   * Kept for the one reason the server carries it: a termination cascade that ran and then failed to
   * record its stage has already revoked five classes of access, and a client that dropped the tally
   * would render "the server refused, nothing changed" over a tenant whose connector is dead.
   */
  readonly revoked?: RevocationTally;
}

export class ConflictError extends ApiError {
  readonly body: ConflictBody;
  constructor(requestUid: string, body: ConflictBody) {
    super(409, requestUid, `conflict:${body.error}`);
    this.name = 'ConflictError';
    this.body = body;
  }
  /** The server's reason code. A mapper turns it into words; nothing renders it. */
  get error(): string {
    return this.body.error;
  }
}

/**
 * A 400 the HANDLER produced, WITH the body it produced.
 *
 * A 400 used to become a bare `ApiError` and its body was dropped, which meant a handler that had
 * deliberately distinguished one refusal from another could not be heard. `auth.invitation.accept`
 * is exactly that case: it answers `password_too_short` separately from the uniform
 * `invitation_not_valid`, and the separation is safe because the length is checked BEFORE the token
 * is looked at, so the answer is identical for a live token and one that never existed. Without the
 * body, an invitee who typed eleven characters was told their live invitation was invalid.
 *
 * The body is carried as `unknown`. Every screen that reads it narrows it to a code IT recognises,
 * so a refusal nobody has taught a screen about falls through to the uniform wording rather than
 * being rendered — which is the direction a disclosure mistake must fail in.
 */
export class BadRequestError extends ApiError {
  readonly body: unknown;

  constructor(requestUid: string, body: unknown) {
    super(400, requestUid, 'invalid_request');
    this.name = 'BadRequestError';
    this.body = body;
  }
}

/** A transport failure. Distinct from a denial so the UI can offer "ניסיון חוזר" honestly. */
export class NetworkError extends ApiError {
  constructor() {
    super(0, '', 'network_unreachable');
    this.name = 'NetworkError';
  }
}

/**
 * Registered once by <App>. Keeping the redirect out of this module means the client has no
 * dependency on the router and stays unit-testable in a plain node process.
 */
let unauthenticatedHandler: (() => void) | null = null;
export function onUnauthenticated(handler: () => void): void {
  unauthenticatedHandler = handler;
}

function requestUidOf(res: Response): string {
  return res.headers.get('x-request-uid') ?? '';
}

/**
 * Reads the JSON body once, tolerating a body that is not JSON at all.
 *
 * `Response.json()` may be called only once, and the 403 path now has to look at the body TWICE —
 * to decide whether it is a gate denial or a step-up, and then to parse it. Reading it here keeps
 * that single-read rule mechanical rather than remembered.
 */
async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    // A refusal whose body is unreadable is still a refusal.
    return null;
  }
}

function parseDenyBody(res: Response, parsed: unknown): DenyBody {
  // A denial whose body is unreadable is still a denial. It collapses to the uniform text.
  const fallback: DenyBody = { error: 'not_permitted', requestUid: requestUidOf(res) };
  if (parsed !== null && typeof parsed === 'object') {
    const b = parsed as Partial<DenyBody>;
    const base = {
      error: typeof b.error === 'string' ? b.error : 'not_permitted',
      requestUid: typeof b.requestUid === 'string' ? b.requestUid : fallback.requestUid,
    };
    if (typeof b.reason === 'string' && typeof b.stage === 'string') {
      return { ...base, reason: b.reason as DenyReason, stage: b.stage as AuthzStage };
    }
    if (typeof b.reason === 'string') {
      return { ...base, reason: b.reason as DenyReason };
    }
    return base;
  }
  return fallback;
}

/**
 * A 403 that carries `step_up_required` is the handler's answer, not the gate's — see
 * `StepUpRequiredError`. Recognised by the error CODE and never by the absence of `reason`, because
 * an undisclosed gate denial also has no reason and the two must not converge.
 */
function stepUpNeedFrom(parsed: unknown): string | null | undefined {
  if (parsed === null || typeof parsed !== 'object') return undefined;
  const b = parsed as Record<string, unknown>;
  if (b['error'] !== 'step_up_required') return undefined;
  return typeof b['need'] === 'string' ? b['need'] : null;
}

/** Names the fields `ConflictBody` declares, and nothing else. An unknown field is dropped. */
function stringArrayOf(value: unknown): readonly string[] | undefined {
  return Array.isArray(value) && value.every((v) => typeof v === 'string')
    ? (value as readonly string[])
    : undefined;
}

function stringOf(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function numberOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * The revocation tally, ALL FIVE CLASSES OR NONE.
 *
 * A partial tally would be worse than no tally: "sessions: 40" with the connector count missing reads
 * as a complete answer and is not one. The five classes are exhaustive of every way a request can
 * arrive authenticated in a tenant, so a body carrying four of them is a body this build cannot
 * report faithfully, and it is dropped rather than half-rendered.
 *
 * Exported because the SUCCESS body of a termination carries the same shape, and one reader for both
 * is the point — a second copy in a screen is where the two would drift.
 */
export function revocationTallyOf(value: unknown): RevocationTally | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const counts: Record<string, number> = {};
  for (const key of REVOCATION_CLASSES) {
    const count = numberOf(raw[key]);
    if (count === undefined) return undefined;
    counts[key] = count;
  }
  return counts as unknown as RevocationTally;
}

/**
 * Reads a conflict body FIELD BY FIELD rather than spreading it.
 *
 * The API side enumerates the same way and for the same reason: *"a result type that leaks onto the
 * wire by accident is how a commercial fact reaches a screen that must never show one."* The mirror
 * of that rule on this side is that a field the server starts sending cannot reach a screen until
 * somebody adds it here and gives it Hebrew.
 */
function parseConflictBody(parsed: unknown): ConflictBody {
  // A conflict whose body is unreadable is still a conflict, and it still wrote nothing.
  {
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const b = parsed as Record<string, unknown>;
      const optional = {
        excess: stringArrayOf(b['excess']),
        missingConfiguration: stringArrayOf(b['missingConfiguration']),
        from: stringOf(b['from']),
        currentStatus: stringOf(b['currentStatus']),
        allowedTransitions: stringArrayOf(b['allowedTransitions']),
        permittedFrom: stringArrayOf(b['permittedFrom']),
        permittedStates: stringArrayOf(b['permittedStates']),
        current: numberOf(b['current']),
        limit: numberOf(b['limit']),
        upgradePath: stringOf(b['upgradePath']),
        resolvingFactRequired: stringOf(b['resolvingFactRequired']),
        candidateCount: numberOf(b['candidateCount']),
        remedy: stringOf(b['remedy']),
        detail: stringOf(b['detail']),
        currentStage: stringOf(b['currentStage']),
        allowedStages: stringArrayOf(b['allowedStages']),
        arcOwner: stringOf(b['arcOwner']),
        violations: stringArrayOf(b['violations']),
        reinstatableUntil: stringOf(b['reinstatableUntil']),
        notBefore: stringOf(b['notBefore']),
        commercialReason: stringOf(b['commercialReason']),
        revoked: revocationTallyOf(b['revoked']),
      };
      const present = Object.entries(optional).filter(([, v]) => v !== undefined);
      return {
        error: stringOf(b['error']) ?? 'conflict',
        ...Object.fromEntries(present),
      } as ConflictBody;
    }
  }
  return { error: 'conflict' };
}

/**
 * TURNS A `Response` INTO EITHER A VALUE OR THE RIGHT ERROR. **CM-013.**
 *
 * ── WHY THIS MODULE NO LONGER ISSUES THE REQUEST ────────────────────────────────────────────────
 *
 * It used to export `request` / `get` / `post`, and a guardrail forbade every other module from
 * importing them. Three mutations defeated that guardrail with CI green: a namespace import, a
 * `./client.js` specifier, and a re-export through a third module. Worse — and nobody had noticed —
 * a screen calling `globalThis.fetch('/api/…')` directly was never checked by that rule at all,
 * because the rule scanned for imports of OUR wrapper rather than for network access.
 *
 * A rule enforced by naming the things that must not be imported can always be beaten by importing
 * them differently. So the transport moved INTO `operations.ts`, the module that is already the
 * choke point, and there is now no exported function anywhere in this app that performs I/O. What
 * remains here is pure: it reads a `Response` somebody else obtained.
 *
 * That shrinks the guardrail's job from *"nobody imports these three names, spelled any of five
 * ways"* to *"the network primitives appear in exactly one file"* — a smaller surface, and one that
 * catches the direct-`fetch` case the old rule could not see.
 *
 * ── AND WHY THE TAXONOMY STAYED HERE ────────────────────────────────────────────────────────────
 *
 * The status-to-error mapping is the vocabulary the whole app refuses in: `DeniedError` carries the
 * gate's reason and stage, `StepUpRequiredError` is deliberately NOT a denial because a step-up is
 * the handler's answer, and `ConflictError` carries the tally. Screens import those classes and
 * their Hebrew. Moving them beside the fetch loop would put the vocabulary inside the mechanism.
 */
export async function interpretResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    unauthenticatedHandler?.();
    throw new UnauthenticatedError(requestUidOf(res));
  }
  if (res.status === 402 || res.status === 403) {
    const parsed = await readJson(res);
    const need = stepUpNeedFrom(parsed);
    // A step-up is the HANDLER's answer and not the gate's, so it does not become a `DeniedError`.
    if (need !== undefined) throw new StepUpRequiredError(requestUidOf(res), need);
    throw new DeniedError(res.status, parseDenyBody(res, parsed));
  }
  if (res.status === 404) {
    throw new NotFoundError(requestUidOf(res));
  }
  if (res.status === 409) {
    throw new ConflictError(requestUidOf(res), parseConflictBody(await readJson(res)));
  }
  if (res.status === 400) {
    throw new BadRequestError(requestUidOf(res), await readJson(res));
  }
  if (!res.ok) {
    throw new ApiError(res.status, requestUidOf(res), 'request_failed');
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * The headers every request carries, and the reasoning for each.
 *
 * Built here rather than in the registry because these are security decisions with an argument
 * behind them, and the registry's job is WHICH endpoint rather than HOW a request is shaped.
 */
export function requestInit(options: {
  readonly method: string;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}): RequestInit {
  return {
    method: options.method,
    // First-party cookie. No Authorization header is ever constructed here.
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      // Forces a CORS preflight for cross-origin attempts, so a form POST from another site
      // cannot reach a write. The server's CSRF stage is the actual control; this is defence in depth.
      'x-requested-with': 'glpoint-web',
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  };
}
